import React, { useEffect, useRef, useState } from 'react';
import styles from '../../style/styles/ProductView.module.css';
import { useChatStateStore, useMessagesStore, useProductsStore, useUserStore } from '../../zustand/store';
import { useExampleQuery } from '../../services/queries/chatbotQueries';
import { PaginationDots, LineIndicator } from './PaginationDots';
import { ProductShrinkView } from './ProductShrinkView';
import { CarouselListVertical, CarouselSingle, CarouselListHorizontal } from './CarouselList';
import { findObjectPosition } from '../../utils/utils';
import { LayoutModeEnum } from '../../type/modeState.type';

export const CarouselContainer = (props) => {
    const { isDisplayOpen, type } = props;
    const { products, structuredProducts } = useProductsStore();
    const { messages } = useMessagesStore();
    const { isMobileDevice } = useUserStore();
    const { 
        currentItemId, 
        currentShrinkCardIndex, 
        currentListCardIndex, 
        setCurrentItemId, 
        setCurrentShrinkCardIndex, 
        setCurrentListCardIndex, 
        isNewProduct, 
        isStreaming, 
        layoutMode 
    } = useChatStateStore();
    const { refetch: refetchExample } = useExampleQuery();
    
    const carouselRef = useRef(null);
    const containerRef = useRef(null);
    const [activeDotIndex, setActiveDotIndex] = useState(0);
    const [currentIndex, setCurrentIndex] = useState(currentShrinkCardIndex);
    const [startX, setStartX] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [cardWidth, setCardWidth] = useState(window.innerWidth);
    const [isScrolling, setIsScrolling] = useState(false);
    const LIST_TYPES = ['horizontal', 'vertical', 'list'];
    const isListType = LIST_TYPES.includes(type);

    useEffect(() => {
        const recalculateWidth = () => {
            const carouselWrapElement = document.getElementById('carousel-wrap');
            const index = isDisplayOpen && isListType ? currentListCardIndex : currentShrinkCardIndex; 

            const currentCardWidth = window.innerWidth;
            setCardWidth(currentCardWidth);

            const carouselCardWidth = 
                isDisplayOpen ? 
                (
                    isListType ?
                    (structuredProducts?.length ? currentCardWidth * structuredProducts.length : currentCardWidth) :
                    (products?.length ? currentCardWidth * products.length : currentCardWidth)
                ) : 
                (products?.length ? currentCardWidth * products.length : currentCardWidth);

            setActiveDotIndex(index);
            setCurrentIndex(index);
            
            if (carouselWrapElement) {
                updateCardIndex(index);
                
                carouselWrapElement.scrollTo({
                    left: index * currentCardWidth,
                    behavior: 'instant'
                });

                if (isDisplayOpen) {
                    document.documentElement.style.setProperty('--carousel-wrap-width', carouselCardWidth + 'px');
                } else {
                    document.documentElement.style.setProperty('--carousel-wrap-width-shrink', carouselCardWidth + 'px');
                }
            }
        };

        const timeoutId = setTimeout(() => {
            requestAnimationFrame(recalculateWidth);
        }, 0);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [isDisplayOpen, isNewProduct, structuredProducts, products, cardWidth]);

    useEffect(() => {
        const handleResize = () => {
            setCardWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        const newItemIds = [];
        if (isDisplayOpen && isListType) {
            if (structuredProducts && structuredProducts.length > 0 && 
                structuredProducts[currentListCardIndex] && 
                structuredProducts[currentListCardIndex].length > 0) {
                structuredProducts[currentListCardIndex].forEach(product => {
                    if (product && product.itemId) {
                        newItemIds.push(String(product.itemId));
                    }
                });
            }
        } else {
            if (products && products.length > 0 && 
                products[currentShrinkCardIndex] && 
                products[currentShrinkCardIndex].itemId) {
                newItemIds.push(String(products[currentShrinkCardIndex].itemId));
            }
        }
        setCurrentItemId(newItemIds);
    }, [currentListCardIndex, currentShrinkCardIndex, isNewProduct]);

    const updateCardIndex = (index) => {
        if (isDisplayOpen && isListType) {
            setCurrentListCardIndex(index);
            if (structuredProducts && structuredProducts.length > 0 && structuredProducts[index] && structuredProducts[index][0]) {
                const foundIndex = findObjectPosition([products], structuredProducts[index][0]?.itemId);
                setCurrentShrinkCardIndex(foundIndex[1]);
            }
        } else {
            setCurrentShrinkCardIndex(index);
            if (products && products.length > 0 && products[index] && structuredProducts && structuredProducts.length > 0) {
                const foundIndex = findObjectPosition(structuredProducts, products[index]?.itemId);
                setCurrentListCardIndex(foundIndex[0]);
            }
        }
        setActiveDotIndex(index);
    }

    const getEventX = (e) => {
        if (e?.type?.includes('mouse')) {
            return e.pageX;
        }
        return e?.type === 'touchend' ? e.changedTouches[0].pageX : e.touches[0].pageX;
    };

    const startDragging = (e) => {
        if (!isMobileDevice) return;
        setIsDragging(true);
        setStartX(getEventX(e));
    };

    const drag = (e) => {
        if (!isMobileDevice) return;
        if (!isDragging) return;

        const currentX = getEventX(e);
        const diff = currentX - startX;
        const len = isDisplayOpen ? structuredProducts.length : products.length;

        // Limit dragging at the edges
        if (
            (currentIndex === 0 && diff > 0) ||
            (currentIndex === len - 1 && diff < 0)
        ) {
            return;
        }
    };

    const endDragging = (e) => {
        // for only mobile device
        if (!isMobileDevice) return;
        if (!isDragging) return;
        const carouselWrapElement = document.getElementById('carousel-wrap');
        
        setIsDragging(false);
        
        const currentX = getEventX(e);
        const diff = currentX - startX;
        const threshold = cardWidth * 0.1;
        const cardNumber = isDisplayOpen && isListType ? structuredProducts.length : products.length;
        
        if (Math.abs(diff) > threshold) {
            if (diff > 0 && currentIndex > 0) {
                setCurrentIndex(currentIndex - 1);
                updateCardIndex(currentIndex - 1);
                carouselWrapElement.scrollTo({
                    left: (currentIndex - 1) * cardWidth,
                    behavior: 'smooth'
                });
            } else if (diff < 0 && currentIndex < cardNumber - 1) {
                setCurrentIndex(currentIndex + 1);
                updateCardIndex(currentIndex + 1);
                carouselWrapElement.scrollTo({
                    left: (currentIndex + 1) * cardWidth,
                    behavior: 'smooth'
                });
            }

        } else {
            carouselWrapElement.scrollTo({
                left: currentIndex * cardWidth,
                behavior: 'smooth'
            });
        }
    };

    const handleCarouselPrev = () => {
        if (isScrolling || currentIndex <= 0) return;
        setIsScrolling(true);
        const carouselWrapElement = document.getElementById('carousel-wrap');

        setCurrentIndex(currentIndex - 1);
        updateCardIndex(currentIndex - 1);

        carouselWrapElement.scrollTo({
            left: (currentIndex - 1) * cardWidth,
            behavior: 'instant'
        });

        setTimeout(() => {
            setIsScrolling(false);
        }, 500);
    };

    const handleCarouselNext = () => {
        if (isScrolling) return;
        setIsScrolling(true);
        const carouselWrapElement = document.getElementById('carousel-wrap');
        if (isDisplayOpen && isListType) {
            if (currentIndex < structuredProducts.length - 1) {
                setCurrentIndex(currentIndex + 1);
                updateCardIndex(currentIndex + 1);
                carouselWrapElement.scrollTo({
                    left: (currentIndex + 1) * cardWidth,
                    behavior: 'instant'
                });
            }
        } else {
            if (currentIndex < products.length - 1) {
                setCurrentIndex(currentIndex + 1);
                updateCardIndex(currentIndex + 1);
                carouselWrapElement.scrollTo({
                    left: (currentIndex + 1) * cardWidth,
                    behavior: 'instant'
                });
            }
        }

        setTimeout(() => {
            setIsScrolling(false);
        }, 500);
    };

    useEffect(() => {
        if (!isScrolling && currentItemId.length > 0 && !isStreaming) {
            refetchExample();
        }
    }, [currentItemId, isScrolling]);

    return (
        <div
            className={styles.CarouselContainer__container}
            ref={containerRef}
            onContextMenu={(e) => e.preventDefault()}
        >
            <div 
                id='carousel-wrap' 
                className={isDisplayOpen ? styles.CarouselContainer__scrollBox : styles.CarouselContainer__scrollBox__shrink}
                style={{ overflowX: !isMobileDevice && 'hidden'}}
            >
                <div
                    
                    className={isDisplayOpen ? styles.CarouselContainer__wrap : styles.CarouselContainer__wrap__shrink}
                    ref={carouselRef}
                    onMouseDown={startDragging}
                    onTouchStart={startDragging}
                    onMouseMove={drag}
                    onTouchMove={drag}
                    onMouseUp={endDragging}
                    onTouchEnd={endDragging}
                    onMouseLeave={() => isDragging && endDragging()}
                >
                    {
                        isDisplayOpen ?
                        (
                            isListType ?
                            (
                                type === 'horizontal' ?
                                (structuredProducts.map((products, idx) => (
                                <div key={idx} style={{width: '100%', padding: !isMobileDevice ? '0 56px' : '0 16px'}}>
                                    <CarouselListHorizontal products={products} idx={idx} /> 
                                </div>
                            ))) :
                            (structuredProducts.map((products, idx) => (
                                <div key={idx} style={{width: '100%', padding: !isMobileDevice ? '0 32px' : '0 16px'}}>
                                    <CarouselListVertical products={products} idx={idx} /> 
                                </div>
                            )))
                        ) :
                            <CarouselSingle products={products} />
                        ):
                        <ProductShrinkView singleProductList={products} />
                    }
                </div>
                {
                    !isMobileDevice && 
                    (
                        (isDisplayOpen && isListType) ? structuredProducts.length > 1 : products.length > 1) &&
                    <>
                        <img 
                            src={`${currentIndex === 0 ? '/img/units/carousel-prev-inactive.png' : '/img/units/carousel-prev-active.png'}`} 
                            width={32} height={32} 
                            style={{position: 'absolute', top: isDisplayOpen ? '59px' : '9px', left: '16px', cursor: currentIndex === 0 ? 'not-allowed' : 'pointer'}} 
                            onClick={handleCarouselPrev}
                            alt='prev'
                        />
                        <img 
                            src={`${currentIndex === (isDisplayOpen && isListType ? structuredProducts.length - 1 : products.length - 1) ? '/img/units/carousel-next-inactive.png' : '/img/units/carousel-next-active.png'}`} 
                            width={32} height={32} 
                            style={{position: 'absolute', top: isDisplayOpen ? '59px' : '9px', right: '16px', cursor: currentIndex === (isDisplayOpen && isListType ? structuredProducts.length - 1 : products.length - 1) ? 'not-allowed' : 'pointer'}} 
                            onClick={handleCarouselNext}
                            alt='next'
                        />
                    </>
                }
            </div>
            {
                <div className={styles.ProductCard__paginationContainer}>
                    {
                        (
                            (
                                isDisplayOpen && structuredProducts.length > 1
                            ) || (
                                !isDisplayOpen && products.length > 1
                            ) || (
                                !isListType && products.length > 1
                            )
                        ) &&
                        <LineIndicator maxValue={isDisplayOpen && isListType ? structuredProducts.length : products.length} value={activeDotIndex} />
                    }
                </div>
            }
        </div>
    );
};
